import { EmailUtil } from "@mcleod/core";
import { Panel } from "../../components/panel/Panel";
import { Textbox } from "../../components/textbox/Textbox";
import { Switch } from "../../components/switch/Switch";
import { PanelProps } from "../..";

interface EmailDialogProps extends PanelProps {
    defaultRecipient?: string;
    copyToSelf?: boolean;
    defaultSubject?: string;
    defaultMessage?: string;
}

export class EmailDialog extends Panel {
    recipientTextbox: Textbox;
    switchCopyToSelf: Switch;
    subjectTextbox: Textbox;
    messageTextbox: Textbox;

    constructor(props: Partial<EmailDialogProps> = {}) {
        super({ id: "panelEmailDialog", height: 400, width: 500, fillRow: true, ...props });

        this.recipientTextbox = new Textbox({
            id: "textboxRecipient",
            caption: "Recipient(s)",
            tooltip: "Enter email addresses separated by commas",
            rowBreak: false,
            text: props.defaultRecipient || ""
        });
        this.recipientTextbox.addlValidationCallback = this.validateEmail;

        this.switchCopyToSelf = new Switch({
            id: "switchCopyToSelf",
            caption: "Copy me on this email",
            tooltip: "Check to send a copy of this email to yourself",
            checked: props.copyToSelf ?? true,
            leftCaption: "No",
            rightCaption: "Yes"
        });
        this.switchCopyToSelf.addChangeListener(() => this.recipientTextbox.validate(true));

        this.subjectTextbox = new Textbox({
            id: "textboxSubject",
            caption: "Subject",
            tooltip: "Enter the subject of the email",
            fillRow: true,
            text: props.defaultSubject || "Email Subject",
            required: true
        });

        this.messageTextbox = new Textbox({
            id: "textboxMessage",
            caption: "Message",
            tooltip: "Enter the body of the email",
            fillHeight: true,
            fillRow: true,
            multiline: true,
            text: props.defaultMessage || "",
            required: true
        });

        this.add(this.recipientTextbox);
        this.add(this.switchCopyToSelf);
        this.add(this.subjectTextbox);
        this.add(this.messageTextbox);
    }

    private validateEmail = () => {
        const emails = this.recipientTextbox.text.split(',').map(email => email.trim());
        const isRecipientEmpty = emails.length === 1 && emails[0] === "";
        const isCCSwitchChecked = this.switchCopyToSelf.checked;

        if (isRecipientEmpty && !isCCSwitchChecked) {
            return {
                isValid: false,
                component: this,
                validationMessage: "A recipient is required if not copying yourself"
            };
        } else if (!isRecipientEmpty && !emails.every(email => EmailUtil.isValidEmail(email))) {
            return {
                isValid: false,
                component: this.recipientTextbox,
                validationMessage: "One or more email addresses are invalid"
            };
        }

        return null;
    }
}
